import { FC } from 'react';
import { observer } from 'mobx-react';

import { GridCellProps, GridFilterOperators } from '@progress/kendo-react-grid';

import { Button, Icon, Stack, StatusLight, Tooltip } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { StandardColumnMenuFilter, Table, TableColumn } from '@servicetitan/table';
import { tokens } from '@servicetitan/tokens/core';

import { SecretManagementOption } from '../../api/app.api';
import * as Styles from '../assets/app-client-table.module.scss';
import {
    AppClientListPageStore,
    AppClientResponseModel,
} from '../stores/app-client-list-page.store';

const ResourceOwnerCell: FC<GridCellProps> = props => {
    const { resourceOwnerTerm, resourceOwnerText } = props.dataItem as AppClientResponseModel;
    return (
        <td>
            <Stack direction="row" alignItems="center">
                {resourceOwnerTerm && (
                    <Stack.Item shrink={0}>
                        <Tooltip el="div" text={resourceOwnerTerm} portal>
                            <Icon
                                name="group_work"
                                color={tokens.colorOrange400}
                                className={Styles.centeredIcon}
                            />
                        </Tooltip>
                    </Stack.Item>
                )}
                <Stack.Item fill>{resourceOwnerText}</Stack.Item>
            </Stack>
        </td>
    );
};
ResourceOwnerCell.displayName = 'ResourceOwnerCell';

const ClientSecretCell: FC<GridCellProps> = props => {
    const [store] = useDependencies(AppClientListPageStore);
    const client = props.dataItem as AppClientResponseModel;
    const { clientId, clientSecrets, enabled } = client;
    return (
        <td>
            {!clientId ? (
                'Not connected yet'
            ) : !enabled ? (
                'Not available yet'
            ) : clientSecrets.length === 0 ? (
                store.secretManagementOption === SecretManagementOption.Developer ? (
                    <Button
                        primary
                        xsmall
                        onClick={() => {
                            store.selectClient(client);
                        }}
                    >
                        Generate
                    </Button>
                ) : (
                    'Not available yet'
                )
            ) : (
                <Stack direction="column">
                    {clientSecrets.map(secret => (
                        <Stack key={secret.id} direction="row" alignItems="center">
                            <Stack.Item shrink={0}>
                                <Icon name="vpn_key" className={Styles.centeredIcon} />
                            </Stack.Item>
                            <Stack.Item fill>{secret.valuePrefix}...</Stack.Item>
                        </Stack>
                    ))}
                    {store.secretManagementOption === SecretManagementOption.Developer && (
                        <Stack.Item>
                            <Button
                                xsmall
                                onClick={() => {
                                    store.selectClient(client);
                                }}
                            >
                                Manage
                            </Button>
                        </Stack.Item>
                    )}
                </Stack>
            )}
        </td>
    );
};
ClientSecretCell.displayName = 'ClientSecretCell';

const EnabledCell: FC<GridCellProps> = props => {
    const { enabled } = props.dataItem as AppClientResponseModel;
    return (
        <td>
            <Tooltip text={enabled ? 'Allowed' : 'Disallowed'} portal>
                <StatusLight color={enabled ? 'success' : 'default'} disabled={!enabled} />
            </Tooltip>
        </td>
    );
};
EnabledCell.displayName = 'EnabledCell';

const filterOperators: GridFilterOperators = {
    text: [
        { text: 'grid.filterEqOperator', operator: 'eq' },
        { text: 'grid.filterContainsOperator', operator: 'contains' },
        { text: 'grid.filterStartsWithOperator', operator: 'startswith' },
        { text: 'grid.filterEndsWithOperator', operator: 'endswith' },
    ],
    numeric: [
        { text: 'grid.filterEqOperator', operator: 'eq' },
        { text: 'grid.filterNotEqOperator', operator: 'neq' },
        { text: 'grid.filterGteOperator', operator: 'gte' },
        { text: 'grid.filterLteOperator', operator: 'lte' },
    ],
};

export interface AppClientTableProps {
    resourceOwnerTerm: string;
}

export const AppClientTable: FC<AppClientTableProps> = observer(({ resourceOwnerTerm }) => {
    const [store] = useDependencies(AppClientListPageStore);

    return (
        <Table tableState={store.tableState} filterOperators={filterOperators} sortable>
            <TableColumn
                field="resourceOwnerText"
                title={`${resourceOwnerTerm}${resourceOwnerTerm === 'Tenant' ? '/Network' : ''} ID`}
                width={175}
                columnMenu={StandardColumnMenuFilter}
                cell={ResourceOwnerCell}
            />
            <TableColumn
                field="clientIdText"
                title="Client ID"
                width={250}
                columnMenu={StandardColumnMenuFilter}
            />
            <TableColumn title="Client Secret" width={175} cell={ClientSecretCell} />
            <TableColumn
                field="currentScopesVersion"
                title="API Scopes Version"
                width={175}
                columnMenu={StandardColumnMenuFilter}
                filter="numeric"
            />
            <TableColumn
                field="enabled"
                title={`Allowed by ${resourceOwnerTerm}`}
                width={175}
                cell={EnabledCell}
            />
        </Table>
    );
});
AppClientTable.displayName = 'AppClientTable';
